import {
  BookingData,
  BookingInProgressData,
  CarInfo,
  FreePeriod,
  ParkingBookingData,
  PlaceType,
  RoomType,
} from 'src/types';
import { b64toBlob } from 'src/utils/b64toBlob';

import { client } from './client';
import { RoomPlaceShort } from './places';
import { Building, Floor, Town } from './types';

export interface ReadTownsResponse {
  towns: Town[];
}

export const readTowns = async (regionSearchText?: string): Promise<Town[]> => {
  try {
    const response = await client.get<ReadTownsResponse>(
      '/api/v2/plans/towns',
      {
        params: {
          regionSearchText,
        },
      },
    );
    return response.data.towns;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateTownParams {
  townName?: string;
  ownEnabled?: boolean;
  ownDisabledReason?: string;
  testData?: boolean;
  timeZone?: number;
}

export const createTown = async (params: CreateTownParams): Promise<Town> => {
  try {
    const response = await client.post<Town>('/api/v2/plans/towns', params);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateTownParams {
  townId: number;
  townName?: string;
  ownEnabled?: boolean;
  ownDisabledReason?: string;
  testData?: boolean;
  timeZone?: number;
}

export const updateTown = async (params: UpdateTownParams): Promise<Town> => {
  try {
    const response = await client.put<Town>('/api/v2/plans/towns', params);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteTown = async (id: number): Promise<void> => {
  try {
    await client.delete(`/api/v2/plans/towns/${id}`);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadBuildingsResponse {
  buildings: Building[];
}

export const readBuildings = async (townId: number): Promise<Building[]> => {
  try {
    const response = await client.get<ReadBuildingsResponse>(
      '/api/v2/plans/buildings',
      {
        params: {
          townId,
        },
      },
    );
    return response.data.buildings;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateBuildingParams {
  townId: number;
  address: string;
  ownEnabled: boolean;
  ownDisabledReason: string;
  testData: boolean;
}

export const createBuilding = async (
  params: CreateBuildingParams,
): Promise<Building> => {
  try {
    const response = await client.post<Building>(
      '/api/v2/plans/buildings',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateBuildingParams {
  buildingId: number;
  townId: number;
  address: string;
  ownEnabled: boolean;
  ownDisabledReason: string;
  testData: boolean;
}

export const updateBuilding = async (
  params: UpdateBuildingParams,
): Promise<Building> => {
  try {
    const response = await client.put<Building>(
      '/api/v2/plans/buildings',
      params,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteBuilding = async (id: number): Promise<void> => {
  try {
    await client.delete(`/api/v2/plans/buildings/${id}`);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadFloorsResponse {
  floors: Floor[];
}

export const readFloors = async (buildingId: number): Promise<Floor[]> => {
  try {
    const response = await client.get<ReadFloorsResponse>(
      '/api/v2/plans/floors',
      {
        params: {
          buildingId,
        },
      },
    );
    return response.data.floors;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface CreateFloorParams {
  townId: number;
  buildingId: number;
  title: string;
  ownEnabled: boolean;
  ownDisabledReason: string;
  testData: boolean;
  isParking: boolean;
}

export const createFloor = async (
  params: CreateFloorParams,
): Promise<Floor> => {
  try {
    const response = await client.post<Floor>('/api/v2/plans/floors', params);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateFloorParams {
  floorId: number;
  townId: number;
  buildingId: number;
  title: string;
  ownEnabled: boolean;
  ownDisabledReason: string;
  testData: boolean;
  isParking: boolean;
}

export const updateFloor = async (
  params: UpdateFloorParams,
): Promise<Floor> => {
  try {
    const response = await client.put<Floor>('/api/v2/plans/floors', params);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteFloor = async (id: number): Promise<void> => {
  try {
    await client.delete(`/api/v2/plans/floors/${id}`);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface BackgroundData {
  url: string;
  width: number;
  height: number;
}

export const getPlanBackground = async (
  floorId: number,
): Promise<BackgroundData | undefined> => {
  try {
    const response = await client.get(`/api/v2/plans/backgrounds/${floorId}`);

    if (!response.data.encodedFileBytes) return;

    const imageBlob = await b64toBlob(
      response.data.encodedFileBytes,
      response.data.mimeType,
    );
    const url = URL.createObjectURL(imageBlob);

    return {
      url,
      width: response.data.imageWidth,
      height: response.data.imageHeight,
    };
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface DeskPlaceWithStatus {
  placeId: number;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Desk;
  placeCurrentStatus: BookingData | null;
  byWindow: boolean;
  withMonitor: boolean;
  withCharger: boolean;
  withDivider: boolean;
  spaceId: number | null;
  responsible: string | null;
}

export interface RoomPlaceWithStatus {
  placeId: number;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Room;
  placeCurrentStatus: BookingData | null;
  roomTypeId: RoomType;
  seatCount: number;
  withTv: boolean;
  withVideoCalls: boolean;
  withFlipChart: boolean;
  withWater: boolean;
  spaceId: number | null;
}

export interface ParkingPlaceWithStatus {
  placeId: number;
  planGeometry: string;
  title: string;
  typeId: PlaceType.Parking;
  placeCurrentStatus: ParkingBookingData | null;
  guest: boolean;
  shared: boolean;
  spaceId: number | null;
  responsible: string | null;
  spaceAdministrator: string | null;
}

export type PlaceWithStatus =
  | DeskPlaceWithStatus
  | RoomPlaceWithStatus
  | ParkingPlaceWithStatus;

export interface ReadDeskPlaceMicroCardsParams {
  floorId: number;
  searchFrom: string;
  searchTo: string;
}

export interface ReadDeskPlaceMicroCardsResponse {
  deskPlaceMicroCards: DeskPlaceWithStatus[];
}

export const readDeskPlaceMicroCards = async (
  params: ReadDeskPlaceMicroCardsParams,
): Promise<DeskPlaceWithStatus[]> => {
  try {
    const response = await client.get<ReadDeskPlaceMicroCardsResponse>(
      '/api/v2/plans/deskPlaceMicroCards',
      { params },
    );
    return response.data.deskPlaceMicroCards;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadRoomPlaceStatusesParams {
  placeId: number;
  searchFrom: string;
  searchTo: string;
}

export interface ReadRoomPlaceStatusesResponse {
  bookedPeriods: BookingData[];
  freePeriods: FreePeriod[];
}

export const readRoomPlaceStatuses = async (
  params: ReadRoomPlaceStatusesParams,
): Promise<ReadRoomPlaceStatusesResponse> => {
  try {
    const response = await client.get<ReadRoomPlaceStatusesResponse>(
      '/api/v2/plans/roomPlaceStatuses',
      { params },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadDeskPlaceMiniCardParams {
  placeId: number;
  searchFrom: string;
  searchTo: string;
}

export interface DeskPlaceMiniCardData {
  placeId: number;
  placeCurrentStatus: BookingData | null;
  title: string;
  byWindow: boolean;
  withMonitor: boolean;
  withCharger: boolean;
  withDivider: boolean;
}

export const readDeskPlaceMiniCard = async (
  params: ReadDeskPlaceMiniCardParams,
): Promise<DeskPlaceMiniCardData> => {
  try {
    const response = await client.get<DeskPlaceMiniCardData>(
      '/api/v2/plans/deskPlaceMiniCard',
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadRoomPlaceMicroCardsParams {
  floorId: number;
  searchFrom: string;
  searchTo: string;
  withAdEmail: boolean;
}

export interface ReadRoomPlaceMicroCardsResponse {
  roomPlaceMicroCards: RoomPlaceWithStatus[];
}

export const readRoomPlaceMicroCards = async (
  params: ReadRoomPlaceMicroCardsParams,
): Promise<RoomPlaceWithStatus[]> => {
  try {
    const response = await client.get<ReadRoomPlaceMicroCardsResponse>(
      '/api/v2/plans/roomPlaceMicroCards',
      { params },
    );
    return response.data.roomPlaceMicroCards;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadRoomPlaceInfosParams {
  floorId: number;
  searchFrom: string;
  searchTo: string;
}

export interface ReadRoomPlaceInfosResponse {
  roomPlaceMicroCards: RoomPlaceShort[];
}

export const readRoomPlaceInfos = async (
  params: ReadRoomPlaceInfosParams,
): Promise<RoomPlaceShort[]> => {
  try {
    const response = await client.get<ReadRoomPlaceInfosResponse>(
      '/api/v2/plans/roomPlaceInfos',
      { params },
    );
    return response.data.roomPlaceMicroCards;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadRoomPlaceMiniCardParams {
  placeId: number;
  searchFrom: string;
  searchTo: string;
}

export interface RoomPlaceMiniCardData {
  placeId: number;
  placeCurrentStatus: BookingData | null;
  title: string;
  roomTypeId: RoomType;
  square: number;
  seatCount: number;
  withTv: boolean;
  withVideoCalls: boolean;
  withFlipChart: boolean;
  withWater: boolean;
}

export const readRoomPlaceMiniCard = async (
  params: ReadRoomPlaceMiniCardParams,
): Promise<RoomPlaceMiniCardData> => {
  try {
    const response = await client.get<RoomPlaceMiniCardData>(
      '/api/v2/plans/roomPlaceMiniCard',
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadParkingPlaceMicroCardsParams {
  floorId: number;
  searchFrom: string;
  searchTo: string;
}

export interface ReadParkingPlaceMicroCardsResponse {
  parkingPlaceMicroCards: ParkingPlaceWithStatus[];
}

export const readParkingPlaceMicroCards = async (
  params: ReadParkingPlaceMicroCardsParams,
): Promise<ParkingPlaceWithStatus[]> => {
  try {
    const response = await client.get<ReadParkingPlaceMicroCardsResponse>(
      '/api/v2/plans/parkingPlaceMicroCards',
      { params },
    );
    return response.data.parkingPlaceMicroCards;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadParkingPlaceMiniCardParams {
  placeId: number;
  searchFrom: string;
  searchTo: string;
}

export interface ParkingPlaceMiniCardData {
  placeId: number;
  spaceId: number;
  placeCurrentStatus: ParkingBookingData | null;
  title: string;
  guest: boolean;
}

export const readParkingPlaceMiniCard = async (
  params: ReadParkingPlaceMiniCardParams,
): Promise<ParkingPlaceMiniCardData> => {
  try {
    const response = await client.get<ParkingPlaceMiniCardData>(
      '/api/v2/plans/parkingPlaceMiniCard',
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const savePlanUnderlay = async (
  floorId: number,
  imageFile: File,
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append('floorId', floorId.toString());
    formData.append('imageFile', imageFile);

    await client.put('/api/v2/plans/backgrounds', formData);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface ReadPlaceAllBookingsResponse {
  placeBookings: BookingData[];
}

export const readPlaceAllBookings = async (
  placeId: number,
): Promise<BookingData[]> => {
  try {
    const response = await client.get<ReadPlaceAllBookingsResponse>(
      `/api/v2/plans/placeAllBookings/${placeId}`,
    );
    return response.data.placeBookings;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface LastParkingBookingData {
  bookingId: string;
  bookedFrom: string;
  bookedTo: string;
  responsibleEmployeeNumber: string;
  authorEmployeeNumber: string;
  guestName: string;
  carModel: string;
  carNumber: string;
}

export const readCarInfo = async (employeeNumber: string): Promise<CarInfo> => {
  try {
    const response = await client.get<LastParkingBookingData>(
      `/api/v2/bookings/lastParkingBookingData/${employeeNumber}`,
    );

    return {
      model: response.data.carModel,
      number: response.data.carNumber,
    };
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};
